import { devices_config } from 'src/configs/devices';
import { expandLabelConfig } from 'src/configs/dash-device-labels/presets';
import { resolveDashInfoLabelsConfig } from 'src/configs/dash-device-labels/resolve_config';
import { statusMatchesKey } from 'src/helpers/dash_device_info_labels';
import { capFirstChar } from 'src/helpers/utilities';
import type { Device } from 'src/types/Device';

const EXCLUDED_STATUS_NAMES = /^(state|timer|schedule|last)$/;

export type SidepanelStatusItem = {
	name?: string;
	role?: string;
	value?: unknown;
	icon?: string;
	iconColor?: string;
	iconStyle?: unknown;
	translation?: string;
	main?: unknown;
	hideLabel?: boolean;
	advLabel?: boolean;
	hasButton?: boolean;
	peripheralType?: unknown;
};

export type SidepanelStatusSlots = {
	/** Designated main status from the dash sane-default preset (or `main` flag). */
	main: SidepanelStatusItem | null;
	/** Secondary preset statuses plus anything not listed in the sane defaults. */
	peek: SidepanelStatusItem[];
};

export type BluetoothSensorStatusSlots = {
	/** Centered CircleAvatar status (temperature on H&T, door/motion/etc. otherwise). */
	avatar: SidepanelStatusItem | null;
	/** Overlay HeroValue above the PeekTray (humidity on H&T; unused otherwise). */
	hero: SidepanelStatusItem | null;
	/** Secondary statuses shown as ListItems inside the PeekTray. */
	peek: SidepanelStatusItem[];
};

const isExcludedStatus = (status: SidepanelStatusItem | null | undefined): boolean => {
	if (!status) return true;
	if (status.hideLabel) return true;
	if (typeof status.peripheralType !== 'undefined') return true;
	if (EXCLUDED_STATUS_NAMES.test(status.name || '')) return true;
	return false;
};

export const getSidepanelStatusTitle = (status: SidepanelStatusItem | null | undefined): string => {
	if (!status) return '';
	if (status.translation) return capFirstChar(String(status.translation));
	if (status.name) return capFirstChar(status.name);
	return '';
};

export const getSidepanelStatusValue = (status: SidepanelStatusItem | null | undefined): string => {
	if (!status) return '';
	if (status.value === 0 || status.value) return String(status.value);
	return '';
};

/**
 * Split status_configurator output into a visible main status and peek-tray extras,
 * using the same dash sane-default presets as control cards.
 *
 * Main = first live preset label (door/power/…). Peek = remaining preset labels
 * plus statuses that are not in the preset. A single leftover status means no tray.
 */
export const resolveSidepanelStatusSlots = (
	device: Device | undefined | null,
	statuses: SidepanelStatusItem[] | undefined,
): SidepanelStatusSlots => {
	const empty: SidepanelStatusSlots = { main: null, peek: [] };
	if (!device) return empty;

	const list = (Array.isArray(statuses) ? statuses : []).filter(
		(item) => !isExcludedStatus(item),
	);
	if (!list.length) return empty;

	const config = resolveDashInfoLabelsConfig(devices_config, device);
	const requestedKeys = expandLabelConfig(config);

	const findByKey = (key: string) => list.find((item) => statusMatchesKey(item, key));

	let main: SidepanelStatusItem | null = null;
	const used = new Set<SidepanelStatusItem>();

	if (requestedKeys.length > 0) {
		for (const key of requestedKeys) {
			const found = findByKey(key);
			if (found) {
				main = found;
				used.add(found);
				break;
			}
		}
	}

	if (!main) {
		const flagged = list.find((item) => typeof item.main !== 'undefined');
		if (flagged) {
			main = flagged;
			used.add(flagged);
		}
	}

	const peek: SidepanelStatusItem[] = [];

	if (requestedKeys.length > 0) {
		for (const key of requestedKeys) {
			const found = findByKey(key);
			if (found && !used.has(found)) {
				peek.push(found);
				used.add(found);
			}
		}
	}

	for (const item of list) {
		if (!used.has(item)) {
			peek.push(item);
			used.add(item);
		}
	}

	return { main, peek };
};

/** Status names already shown on TemperatureWheel (`current`). */
export const WALL_DISPLAY_THERMOSTAT_PEEK_EXCLUDE = [
	'temperature',
	'thermostat_temperature',
] as const;

/**
 * Flatten designated main into the peek list (no HeroValue overlay).
 * Used for Wall Display: the switch / thermostat / media control is the main event.
 */
export const peekOnlyStatusSlots = (
	slots: SidepanelStatusSlots,
	options?: { excludeNames?: readonly string[] },
): SidepanelStatusSlots => {
	const exclude = new Set(options?.excludeNames ?? []);
	const peek = [slots.main, ...slots.peek].filter(
		(item): item is SidepanelStatusItem => !!item && !exclude.has(item.name || ''),
	);
	return { main: null, peek };
};

const listLiveStatuses = (
	statuses: SidepanelStatusItem[] | undefined,
): SidepanelStatusItem[] =>
	(Array.isArray(statuses) ? statuses : []).filter((item) => !isExcludedStatus(item));

/**
 * Bluetooth sensor Main-tab slots. H&T (temperature + humidity present):
 * temperature stays on the centered avatar, humidity is the overlay HeroValue,
 * remaining extras go in the PeekTray. Other sensors reuse the dash preset:
 * first main status is the avatar, the rest peek, no overlay hero.
 */
export const resolveBluetoothSensorStatusSlots = (
	device: Device | undefined | null,
	statuses: SidepanelStatusItem[] | undefined,
): BluetoothSensorStatusSlots => {
	const empty: BluetoothSensorStatusSlots = { avatar: null, hero: null, peek: [] };
	if (!device) return empty;

	const list = listLiveStatuses(statuses);
	if (!list.length) return empty;

	const temperature = list.find((item) => item.name === 'temperature');
	const humidity = list.find((item) => item.name === 'humidity');

	if (temperature && humidity) {
		return {
			avatar: temperature,
			hero: humidity,
			peek: list.filter((item) => item !== temperature && item !== humidity),
		};
	}

	const base = resolveSidepanelStatusSlots(device, statuses);
	return {
		avatar: base.main,
		hero: null,
		peek: base.peek,
	};
};
